/**
 * R-10 — Browser-safe constants + pure helpers for the jobExecutions
 * idempotency layer.
 *
 * This file MUST NOT import `firebase-admin/*`, `node:*` modules, or anything
 * else that uses `process.env` or Node-only APIs. Server-only logic
 * (`claimJobExecution`, `completeJobExecution`, `failJobExecution`,
 * `generateJobIdempotencyKey`, `resetJobExecutionForReplay`) lives in
 * `jobIdempotency.ts` and stays out of the client bundle.
 *
 * Why this split exists: `jobIdempotency.ts` has a RUNTIME import of
 * `FieldValue` from `firebase-admin/firestore`. When that file was reachable
 * via `AdminJobExecutions → jobExecutionsAdmin → jobIdempotency`, Vite pulled
 * firebase-admin into the client bundle and the app blew up at runtime with
 * `ReferenceError: process is not defined`. By isolating the constants here,
 * `jobExecutionsAdmin` (client) imports only browser-safe symbols.
 */

/** Firestore collection holding job execution records. */
export const JOB_EXECUTIONS_COLLECTION = 'jobExecutions';

/** Default in-progress lease — stale claims can be taken over after this. */
export const DEFAULT_JOB_EXECUTION_TTL_MS = 24 * 60 * 60 * 1000;

export interface ClaimJobExecutionOpts {
  jobName: string;
  payload?: Record<string, unknown>;
  ttlMs?: number;
  /**
   * Replay a TERMINAL record (completed, or failed non-retryable). This never
   * bypasses a live in-progress lease — a forced request during an active run
   * dedups to that run instead of double-executing (the 2026-07-28 SED
   * double-rebuild class). To steamroll a live lease, use the admin
   * `resetJobExecutionForReplay` explicitly.
   */
  forceReplay?: boolean;
  executionName?: string;
  /**
   * Fingerprint of the job's INPUTS (see JobExecutionRecord.payloadFingerprint).
   * When provided: a completed record with a DIFFERENT fingerprint is claimable
   * without force (inputs changed ⇒ the completed result is stale). A completed
   * record with the SAME fingerprint keeps skipping (`already-completed`).
   */
  payloadFingerprint?: string;
  /**
   * A completed record older than this (by `completedAt`) is claimable without
   * force even when the fingerprint is unchanged — controlled periodic
   * re-execution for jobs whose external inputs age (e.g. provider data).
   * Omit for strictly terminal completed-dedup.
   */
  completedReplayAfterMs?: number;
}

export interface CompleteJobExecutionOpts {
  resultSummary?: Record<string, unknown>;
  /** When set, only this owning attempt may terminally complete the record. */
  expectedAttemptCount?: number;
  /** Platform execution name; retries may finalize their own failed record. */
  expectedExecutionName?: string;
}

export interface FailJobExecutionOpts {
  retryable?: boolean;
  /** When set, only this owning attempt may terminally fail the record. */
  expectedAttemptCount?: number;
  /** Platform execution name; retries may finalize their own failed record. */
  expectedExecutionName?: string;
}

/** Order-independent JSON stringify for stable hashing. Pure / no Node deps. */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map(k => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
}

/** ISO date string for N days ago — used by admin list queries. Pure. */
export function daysAgoIso(days: number, nowMs = Date.now()): string {
  return new Date(nowMs - days * 24 * 60 * 60 * 1000).toISOString();
}
